import type { ApiPromise } from '@polkadot/api'
import type { Bytes, Option, StorageKey } from '@polkadot/types'
import type { QueryFunctionContext } from '@tanstack/query-core'

import { bulkGetOriginOfShells, getEarnedEnergyPoints, getMCPLinkedData, getSpiritLeaderboard, SpiritAttributesResponse } from './requests'

import { isLeft, isRight } from 'fp-ts/Either'
import { hexToString, hexToNumber } from '@polkadot/util'
import { fromPairs, zip, map, prop, keys, add } from 'ramda'

import { formatArweaveUrl } from '@/utils/formatArweaveUrl'
import {
  getMcpSignConfigData,
  fetchSpiritAttributes,
} from './requests'

export function querySpiritAttributes(prefix: string, address?: string) {
  return {
    queryKey: ['spiritAttributes', address],
    queryFn: async (ctx: any) => {
      const {
        queryKey: [, address],
      } = ctx as QueryFunctionContext<[string, string]>
      if (!address) {
        return {} as SpiritAttributesResponse['spirit']
      }
      const result = await fetchSpiritAttributes(prefix, address)
      if (isLeft(result)) {
        // @TODO return a fallback value
        return {} as SpiritAttributesResponse['spirit']
      }
      return result.right
    },
    staleTime: 1_000 * 60 * 5, // 5 mins
  }
}

export function querySpiritCollectionId(api: ApiPromise) {
  return {
    queryKey: ['spiritCollectionId'],
    queryFn: async () => {
      const result = await api.query.pwNftSale.spiritCollectionId()
      return result.toJSON() as number
    },
  }
}

export function queryOriginOfShellCollectionId(api: ApiPromise) {
  return {
    queryKey: ['originOfShellCollectionId'],
    queryFn: async () => {
      const result = await api.query.pwNftSale.originOfShellCollectionId()
      return result.toJSON() as number
    },
  }
}

export function querySpiritNftMetadata(api: ApiPromise) {
  return {
    queryKey: ['spiritNftMetadata'],
    queryFn: async (ctx: any) => {
      const result = await api.query.pwNftSale.spiritsMetadata()
      const url = formatArweaveUrl(hexToString(result.toJSON()))
      const response = await fetch(url)
      const json = await response.json()
      return {
        name: json.name,
        description: json.description,
        mediaUri: formatArweaveUrl(json.mediaUri),
        externalUri: json.externalUri,
        backgroundUri: formatArweaveUrl(json.backgroundUri),
      }
    },
  }
}

export function queryOwnedCollectionAssets(
  api: ApiPromise,
  collectionId: number,
  address: string,
) {
  return {
    queryKey: ['ownedCollectionItems', collectionId, address],
    queryFn: async (ctx: any) => {
      const {
        queryKey: [, collectionId, address],
      } = ctx as QueryFunctionContext<[string, number, string]>
      const uniques = await api.query.uniques.account.entries(
        address,
        collectionId,
      )
      const assetIds = uniques.map(
        ([
          {
            args: [, , rawNftId],
          },
        ]) => rawNftId.toJSON() as number,
      )
      const properties = await Promise.all(
        assetIds.map(assetId =>
          api.query.rmrkCore.properties.entries(collectionId, assetId),
        ),
      )
      const result = zip(assetIds, properties)
      return result
    },
  }
}

export function transformOriginOfShellProperties(
  api: ApiPromise,
  result: [StorageKey, Option<Bytes>][],
) {
  const records = fromPairs(
    result.map(([storageKey, value]) => {
      const [, , propertyName] = storageKey.args.map(i => i.toHuman())
      const hex = hexToNumber(value.toString())
      return [propertyName as string, hex]
    }),
  )
  const career = api.createType('CareerType', records.career).toHuman()
  const rarity = api.createType('RarityType', records.rarity).toHuman()
  const race = api.createType('RaceType', records.race).toHuman()
  return {
    career: career as string,
    rarity: rarity as string,
    race: race as string,
    generation: records.generation,
  }
}

export function queryAllOriginShellMetadata(api: ApiPromise) {
  return {
    queryKey: ['originShellMetadata'],
    queryFn: async (ctx: any) => {
      const result = await api.query.pwNftSale.originOfShellsMetadata.entries()
      const pairs = result.map(
        ([
          {
            args: [key],
          },
          value,
        ]) =>
          [
            key.toJSON() as string,
            formatArweaveUrl(value.toHuman()) as string,
          ] as Pairs<string, string>,
      )
      const responses = await Promise.all(
        pairs.map(([_, url]) => fetch(url).then(response => response.json())),
      )
      const mediaUris = map(
        formatArweaveUrl,
        map<any, string>(prop('mediaUri'), responses),
      )
      const assetResponses = await Promise.all(
        mediaUris.map(url => fetch(url).then(response => response.json())),
      )
      return fromPairs(
        zip(pairs, assetResponses).map(([pair, json]) => {
          return [
            pair[0],
            {
              name: json.name,
              description: json.description,
              externalUri: json.externalUri,
              mediaUri: formatArweaveUrl(json.mediaUri),
              'mediaUri@2x': formatArweaveUrl(json['mediaUri@2x']),
              'mediaUri@3x': formatArweaveUrl(json['mediaUri@3x']),
              backgroundUri: formatArweaveUrl(json.backgroundUri),
              'backgroundUri@2x': formatArweaveUrl(json['backgroundUri@2x']),
              'backgroundUri@3x': formatArweaveUrl(json['backgroundUri@3x']),
              videoUri: formatArweaveUrl(json.videoUri),
              webmUri: formatArweaveUrl(json.webmUri),
            },
          ]
        }),
      )
    },
    staleTime: 1_000 * 60 * 5, // 5 mins
  }
}

export function queryMcpSignConfigData(prefix: string) {
  return {
    queryKey: ['mcpSignConfig', prefix],
    queryFn: async () => {
      return await getMcpSignConfigData(prefix)
    },
    staleTime: 1_000 * 60 * 30, // 30 mins
  }
}

export function queryMcpLinkedData(prefix: string, address: string) {
  return {
    queryKey: ['mcpLinkedData', prefix, address],
    queryFn: async (ctx: any) => {
      const { queryKey: [, prefix, address] } = ctx as QueryFunctionContext<[string, string, string]>
      const result = await getMCPLinkedData(prefix, address)
      if (isRight(result)) {
        return result.right
      }
      return null
    },
    staleTime: 1_000 * 60 * 5, // 5 mins
  }
}

export function querySpiritLeaderboard(prefix: string) {
  return {
    queryKey: ['spiritLeaderboard', prefix],
    queryFn: async (ctx: any) => {
      const { queryKey: [, prefix] } = ctx as QueryFunctionContext<[string, string]>
      const result = await getSpiritLeaderboard(prefix)
      if (isRight(result)) {
        return result.right
      }
      return []
    }
  }
}

export function queryBulkGetOriginShells(prefix: string, addresses: string[]) {
  return {
    queryKey: ['bulkGetOriginShells', prefix, addresses],
    queryFn: async (ctx: any) => {
      const { queryKey: [, prefix, addresses] } = ctx as QueryFunctionContext<[string, string, string[]]>
      const result = await bulkGetOriginOfShells(prefix, addresses)
      if (isRight(result)) {
        return result.right
      }
      return []
    }
  }
}

export function queryEarnedEnergyPoints(prefix: string, address?: string) {
  return {
    queryKey: ['earnedEnergyPoints', address],
    queryFn: async (ctx: any) => {
      const { queryKey: [, address] } = ctx as QueryFunctionContext<[string, string]>
      if (!address) {
        return 0
      }
      const result = await getEarnedEnergyPoints(prefix, address)
      if (isRight(result)) {
        return result.right
      }
      return 0
    },
    refetchInterval: 1_000 * 60, // 1 minutes
  }
}